import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';
import { ConfirmDialogModel, ConfirmDialogService } from '@ui';

import { ProjectLocationService } from '../../services/project-location.service';
import { ProjectLocationActions } from '../state/project-location/project-location.actions';

export const loadProjectLocations = createEffect(
  (actions$ = inject(Actions), projectLocationService = inject(ProjectLocationService)) => {
    return actions$.pipe(
      ofType(ProjectLocationActions.loadLocations),
      switchMap(({ projectId }) => {
        return projectLocationService.getAll(projectId).pipe(
          map((locations) =>
            ProjectLocationActions.loadLocationsSuccess({ locations }),
          ),
          catchError(() => of(ProjectLocationActions.loadLocationsFail())),
        );
      }),
    );
  },
  { functional: true },
);

export const createProjectLocation = createEffect(
  (actions$ = inject(Actions), projectLocationService = inject(ProjectLocationService)) => {
    return actions$.pipe(
      ofType(ProjectLocationActions.createLocation),
      exhaustMap(({ projectId, payload }) => {
        return projectLocationService.create(projectId, payload).pipe(
          map((location) =>
            ProjectLocationActions.createLocationSuccess({ location }),
          ),
          catchError(() => of(ProjectLocationActions.createLocationFail())),
        );
      }),
    );
  },
  { functional: true },
);

export const updateProjectLocation = createEffect(
  (actions$ = inject(Actions), projectLocationService = inject(ProjectLocationService)) => {
    return actions$.pipe(
      ofType(ProjectLocationActions.updateLocation),
      mergeMap(({ locationId, payload }) => {
        return projectLocationService.update(locationId, payload).pipe(
          map((location) =>
            ProjectLocationActions.updateLocationSuccess({ location }),
          ),
          catchError(() => of(ProjectLocationActions.updateLocationFail())),
        );
      }),
    );
  },
  { functional: true },
);

export const requestDeleteProjectLocation = createEffect(
  (
    actions$ = inject(Actions),
    confirmDialogService = inject(ConfirmDialogService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationActions.requestDeleteLocation),
      concatMap(({ locationId }) => {
        return confirmDialogService
          .open({
            type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
            title: 'CREATE_LOCATION_MODAL.DELETE_CONFIRM.TITLE',
            msg: 'CREATE_LOCATION_MODAL.DELETE_CONFIRM.MESSAGE',
            confirm: 'PROJECT_LIST.ACTIONS.DELETE',
            cancel: 'COMMON.CANCEL',
            disableClose: true,
          })
          .pipe(
            map((result) =>
              result?.selection === ConfirmDialogModel.ConfirmResult.OK
                ? ProjectLocationActions.deleteLocation({ locationId })
                : ProjectLocationActions.requestDeleteLocationCancel(),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const deleteProjectLocation = createEffect(
  (actions$ = inject(Actions), projectLocationService = inject(ProjectLocationService)) => {
    return actions$.pipe(
      ofType(ProjectLocationActions.deleteLocation),
      mergeMap(({ locationId }) => {
        return projectLocationService.remove(locationId).pipe(
          map(() => ProjectLocationActions.deleteLocationSuccess({ locationId })),
          catchError(() =>
            of(ProjectLocationActions.deleteLocationFail({ locationId })),
          ),
        );
      }),
    );
  },
  { functional: true },
);
